import { X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/** What Tab can reach inside the panel — nothing in a closed `<details>`. */
function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
    const shut = el.closest('details:not([open])');
    return !shut || el.tagName === 'SUMMARY';
  });
}

/**
 * Minimal accessible modal dialog (overlay + centered panel). Self-contained —
 * no extra dependency; Escape, the ✕ and overlay-click all call `onClose`, body
 * scroll is locked while open.
 *
 * Every way out calls `onClose`, always. A dialog that must not simply vanish
 * (the one-time partner key) decides what closing means in its own `onClose`,
 * where it can say why; a ✕ that silently does nothing reads as broken.
 * For the same reason a caller that refuses closing for a moment (a request in
 * flight) passes `closeDisabled`: the ✕ then renders disabled, like the
 * dialog's own Cancel. Escape and the overlay still reach `onClose`, which
 * keeps the final say.
 *
 * Focus moves in on open (an `autoFocus`/`data-autofocus` element, else the
 * first control), Tab stays inside, and focus returns to what opened it.
 *
 * The panel is capped at 90dvh and SCROLLS INTERNALLY. Without that a tall form
 * simply runs off the bottom of the screen with no way to reach it: the page
 * behind is scroll-locked while the dialog is open, so whatever overflows is
 * unreachable rather than merely below the fold. dvh, not vh, because mobile
 * browser chrome makes vh lie.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  className,
  closeDisabled = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
  /** Render the ✕ disabled while the caller's `onClose` would refuse anyway. */
  closeDisabled?: boolean;
}) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  // Read through a ref so a caller's inline `onClose` doesn't re-run the
  // effect — which would steal focus back to the first field on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      const preferred = panel.querySelector<HTMLElement>('[data-autofocus]');
      (preferred ?? focusables(panel).find((el) => !el.hasAttribute('data-modal-close')) ?? panel).focus();
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panel) return;
      const items = focusables(panel);
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
      if (opener?.isConnected) opener.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-[rgba(28,36,52,0.45)] backdrop-blur-[6px]" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...(description ? { 'aria-describedby': descriptionId } : {})}
        tabIndex={-1}
        className={cn('relative z-10 flex max-h-[90dvh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border bg-card shadow-[var(--shadow-modal)] outline-none', className)}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b px-7 py-5">
          <div>
            <h2 id={titleId} className="text-[17px] font-bold text-secondary-foreground">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="mt-1 text-[13px] text-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            aria-label={t('common.close')}
            data-modal-close=""
            className="grid size-8 shrink-0 place-items-center rounded-xl text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 grow overflow-y-auto px-7 py-6">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
